import { Injectable } from '@angular/core';
import { RxStomp, RxStompConfig } from '@stomp/rx-stomp';
import { Observable, BehaviorSubject, filter, take } from 'rxjs';
import { map } from 'rxjs/operators';
import {environment} from '@/environments/environment';

@Injectable({
	providedIn: 'root'
})
export class WebSocketService {

	private rxStomp: RxStomp;
	private connectionStatus$ = new BehaviorSubject<boolean>(false);

	constructor() {
		this.rxStomp = new RxStomp();
		this.configure();
	}

	private configure(): void {
		const urlFromEnv = environment.wsUrl;

		const config: RxStompConfig = {
			brokerURL: urlFromEnv,
			heartbeatIncoming: 10000,
			heartbeatOutgoing: 10000,
			reconnectDelay: 5000,
		};

		this.rxStomp.configure(config);

		this.rxStomp.connected$.subscribe((state) => {
			if (state) {
				this.connectionStatus$.next(true);
			} else {
				this.connectionStatus$.next(false);
			}
		});
	}

	/**
	 * Active la connexion WebSocket
	 */
	connect(): void {
		if (this.rxStomp.active) {
			return;
		}
		this.rxStomp.activate();
	}

	/**
	 * Désactive la connexion WebSocket
	 */
	disconnect(): void {
		if (!this.rxStomp.active) {
			return;
		}
		this.rxStomp.deactivate();
	}

	/**
	 * Envoie un message au serveur (attend la connexion si nécessaire)
	 */
	send<T>(destination: string, body: T): void {
		if (!this.isConnected()) {
			this.waitForConnection().subscribe(() => {
				this.sendNow(destination, body);
			});

			return;
		}

		this.sendNow(destination, body);
	}

	/**
	 * Envoie immédiatement un message
	 */
	private sendNow<T>(destination: string, body: T): void {
		this.rxStomp.publish({
			destination,
			body: JSON.stringify(body)
		});
	}

	/**
	 * Attend que le WebSocket soit connecté
	 */
	waitForConnection(): Observable<boolean> {
		return this.connectionStatus$.pipe(
			filter(connected => connected === true),
			take(1)
		);
	}

	/**
	 * Écoute un topic et retourne les messages typés
	 */
	watch<T>(topic: string): Observable<T> {

		return this.rxStomp.watch(topic).pipe(
			map(message => {
				return JSON.parse(message.body) as T;
			})
		);
	}

	/**
	 * Observable du statut de connexion
	 */
	getConnectionStatus(): Observable<boolean> {
		return this.connectionStatus$.asObservable();
	}

	/**
	 * Vérifie si le WebSocket est connecté
	 */
	isConnected(): boolean {
		return this.connectionStatus$.value;
	}
}
